import React, {useState} from "react";
import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {Box, Colors, Container, IconButton, IconEdit, IconTrash, Text} from "@bounce/shared/ui";
import {Button} from "../../../ui/components";
import {ParamSchema} from "../../../apis/requests/segments/types";
import ConditionRow from "./ConditionRow";
import {makeDefaultCondition} from "./helpers";
import MatchToggle from "./MatchToggle";
import {ConditionsFormValues} from "./schema";

type Props = {
  groupIndex: number;
  parameters: ParamSchema[];
  onRemove: () => void;
  showRemove: boolean;
};

const GROUP_TITLE_SIZE = 16;
const GROUP_TITLE_FONT_FAMILY = "InterDisplay, sans-serif";

const ConditionGroupCard: React.FC<Props> = ({groupIndex, parameters, onRemove, showRemove}) => {
  const {control, watch} = useFormContext<ConditionsFormValues>();
  const {fields, append, remove} = useFieldArray({
    control,
    name: `groups.${groupIndex}.conditions`,
  });

  const [editing, setEditing] = useState(false);
  const groupName = watch(`groups.${groupIndex}.name`);

  const commitEdit = () => setEditing(false);

  const onAddCondition = () => append(makeDefaultCondition(parameters) as never);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: Colors.bw.white,
        borderRadius: "12px",
        border: `1px solid ${Colors.tertiary._200}`,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Container row fullWidth sx={{justifyContent: "space-between", alignItems: "center"}}>
        {editing ? (
          <Controller
            name={`groups.${groupIndex}.name`}
            control={control}
            render={({field}) => (
              <Box
                component="input"
                {...field}
                onBlur={() => {
                  field.onBlur();
                  commitEdit();
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") commitEdit();
                }}
                placeholder={`Group ${groupIndex + 1}`}
                sx={{
                  border: `1px solid ${Colors.tertiary._300}`,
                  borderRadius: "8px",
                  outline: "none",
                  background: Colors.bw.white,
                  fontSize: GROUP_TITLE_SIZE,
                  fontWeight: 600,
                  fontFamily: GROUP_TITLE_FONT_FAMILY,
                  color: Colors.grey._800,
                  padding: "6px 10px",
                  width: 200,
                  "&::placeholder": {color: Colors.grey._500},
                  "&:focus": {borderColor: Colors.blue._400},
                }}
              />
            )}
          />
        ) : (
          <Box onClick={() => setEditing(true)} sx={{display: "flex", alignItems: "center", gap: 1, cursor: "pointer"}}>
            <Text variant="interDisplay600" size={GROUP_TITLE_SIZE} sx={{color: Colors.grey._900}}>
              {groupName || `Group ${groupIndex + 1}`}
            </Text>
            <Box component={IconEdit} sx={{width: 16, height: 16, color: Colors.grey._400, flexShrink: 0}} />
          </Box>
        )}
        <Container row sx={{gap: 1, alignItems: "center"}}>
          <Controller
            name={`groups.${groupIndex}.match_mode`}
            control={control}
            render={({field}) => <MatchToggle value={field.value} onChange={field.onChange} />}
          />
          {showRemove && (
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{
                border: `1px solid ${Colors.tertiary._300}`,
                borderRadius: "8px",
                width: 36,
                height: 36,
                color: Colors.grey._700,
                "& svg path": {stroke: "currentColor"},
                "&:hover": {backgroundColor: Colors.red._100, borderColor: Colors.red._300, color: Colors.red._700},
              }}
            >
              <Box component={IconTrash} sx={{width: 14, height: 14}} />
            </IconButton>
          )}
        </Container>
      </Container>

      {fields.map((field, index) => (
        <ConditionRow
          key={field.id}
          groupIndex={groupIndex}
          index={index}
          parameters={parameters}
          onRemove={() => remove(index)}
          showLabels={index === 0}
        />
      ))}

      <Button
        buttonType="outlined-primary"
        size="small"
        onClick={onAddCondition}
        disabled={parameters.length === 0}
        sx={{
          backgroundColor: Colors.bw.white,
          "&:hover": {backgroundColor: Colors.blue._100},
        }}
      >
        + Add rule
      </Button>
    </Box>
  );
};

export default ConditionGroupCard;
